import axios, { AxiosError, type InternalAxiosRequestConfig } from "axios";

// İşaretli isteklerde 401 → refresh/logout/redirect mantığı TAMAMEN atlanır.
// Entegrasyon (Trendyol/Yemeksepeti) çağrıları upstream'den 401 dönse bile
// kullanıcı ASLA login'e atılmaz; hata UI'da gösterilir.
declare module "axios" {
  export interface AxiosRequestConfig {
    skipAuthHandling?: boolean;
  }
}

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:3000";

export const api = axios.create({
  baseURL: `${API_BASE}/api`,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Refresh-on-401 logic. We:
//  1. Skip retry for auth endpoints (login/register/etc) so the user sees the
//     real error from the server.
//  2. If a refresh is already in flight, queue subsequent failed requests on
//     the same promise so we never fire two parallel /auth/refresh calls.
//  3. On refresh success, update tokens and replay the original request.
//  4. On refresh failure, clear all auth and redirect to login.
let refreshInFlight: Promise<string> | null = null;

interface RetryConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

async function performRefresh(): Promise<string> {
  const refreshToken = localStorage.getItem("refreshToken");
  if (!refreshToken) throw new Error("NO_REFRESH_TOKEN");

  // Use raw axios so the request interceptor (which adds Bearer header) and
  // response interceptor (which would loop us back here) don't fire.
  const res = await axios.post(`${API_BASE}/api/auth/refresh`, { refreshToken });
  const { token: newToken, refreshToken: newRefresh } = res.data as {
    token: string;
    refreshToken: string;
  };
  localStorage.setItem("token", newToken);
  if (newRefresh) localStorage.setItem("refreshToken", newRefresh);
  return newToken;
}

// Teşhis: logout'a yol açan son auth hatasını localStorage'a yazar. Tam-sayfa
// /login yönlendirmesi Network/Console loglarını uçurduğu için, yönlendirme
// sonrası konsolda `JSON.parse(localStorage.getItem("authDebug"))` ile okunur.
function recordAuthDebug(info: Record<string, unknown>) {
  try {
    localStorage.setItem("authDebug", JSON.stringify({ at: new Date().toISOString(), ...info }));
  } catch {
    /* localStorage yoksa yok say */
  }
}

api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config as RetryConfig | undefined;

    // Muaf istek: ne refresh, ne logout, ne redirect — sadece hatayı geri ver.
    // Trendyol/Yemeksepeti gibi entegrasyon çağrıları 401 dönse bile kullanıcı
    // login'e ATILMAZ. (Çağıran taraf hatayı kendi UI'ında gösterir.)
    if (original?.skipAuthHandling) {
      return Promise.reject(error);
    }

    const url = original?.url || "";
    const isAuthEndpoint =
      url.includes("/auth/login") ||
      url.includes("/auth/register") ||
      url.includes("/auth/forgot") ||
      url.includes("/auth/reset") ||
      url.includes("/auth/verify") ||
      url.includes("/auth/resend") ||
      url.includes("/auth/refresh") ||
      url.includes("/auth/social") ||
      url.includes("/auth/staff-login");

    if (error.response?.status !== 401 || isAuthEndpoint || !original || original._retry) {
      // Final 401 with no retry option → clean up and bounce to login.
      if (error.response?.status === 401 && !isAuthEndpoint) {
        recordAuthDebug({ stage: "final-401", url, status: 401 });
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("staffInfo");
        if (typeof window !== "undefined") window.location.href = "/login";
      }
      return Promise.reject(error);
    }

    if (!localStorage.getItem("refreshToken")) {
      // No refresh token at all — go straight to logout.
      recordAuthDebug({ stage: "no-refresh-token", url });
      localStorage.removeItem("token");
      localStorage.removeItem("staffInfo");
      if (typeof window !== "undefined") window.location.href = "/login";
      return Promise.reject(error);
    }

    original._retry = true;

    try {
      // Coalesce concurrent failures onto a single refresh.
      refreshInFlight = refreshInFlight ?? performRefresh();
      const newToken = await refreshInFlight;
      original.headers.Authorization = `Bearer ${newToken}`;
      return api(original);
    } catch (refreshErr) {
      const re = refreshErr as AxiosError<{ reason?: string; error?: string }>;
      recordAuthDebug({
        stage: "refresh-failed",
        url,
        refreshStatus: re.response?.status,
        refreshReason: re.response?.data?.reason || re.response?.data?.error || re.message,
      });
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("staffInfo");
      if (typeof window !== "undefined") window.location.href = "/login";
      return Promise.reject(refreshErr);
    } finally {
      refreshInFlight = null;
    }
  }
);
